import { AnyAction, createReducer } from '@reduxjs/toolkit';
import {
	clearSensorGroupMap,
	clearSensorGroupPairs,
	clearSensorGroups,
	getSensorGroupMap,
	getSensorGroupMapRejected,
	getSensorGroupMapResolved,
	getSensorGroupPairsRejected,
	getSensorGroupPairsResolved,
	getSensorGroups,
	getSensorGroupsRejected,
	getSensorGroupsResolved,
	getSensors,
	getSensorsRejected,
	getSensorsResolved,
	getSingleSensorGroupById,
	getSingleSensorGroupByIdRejected,
	getSingleSensorGroupByIdResolved,
	upsertSensorGroupConfig,
	upsertSensorGroupConfigRejected,
	upsertSensorGroupConfigResolved,
} from '@Actions/sensorGroup.action';

import { STATUS_ENUM } from '@Types/editSensorData.types';
import { SensorGroupStoreState } from '@Types/sensorGroup.types';

export const initialState: SensorGroupStoreState = {
	sensorGroups: {
		status: STATUS_ENUM.IDLE,
		sensorGroups: [],
	},
	upsertSensorGroup: {
		status: 'idle',
	},
	sensorGroupById: {
		initialized: false,
		configuredSensors: undefined,
		sensorLoaded: false,
	},
	sensorGroupMap: {
		status: STATUS_ENUM.IDLE,
		sensorGroupMap: {},
	},
};

interface SensorGroup {
	[x: string]: (
		state: SensorGroupStoreState,
		action: AnyAction,
	) => SensorGroupStoreState | undefined;
}

const reducer: SensorGroup = {
	[clearSensorGroups.toString()]: () => initialState,
	[getSensorGroups.toString()]: state => ({
		...state,
		sensorGroups: {
			...state.sensorGroups,
			status: STATUS_ENUM.PENDING,
		},
	}),
	[getSensorGroupsResolved.toString()]: (state, params) => {
		return {
			...state,
			sensorGroups: {
				status: STATUS_ENUM.SUCCESS,
				sensorGroups: params.payload.sensorGroups,
				pagination: params.payload.pagination,
			},
		};
	},
	[getSensorGroupsRejected.toString()]: state => ({
		...state,
		sensorGroups: {
			sensorGroups: [],
			status: STATUS_ENUM.ERROR,
		},
	}),
	[getSingleSensorGroupById.toString()]: state => ({
		...state,
		sensorGroupById: {
			initialized: false,
		},
	}),
	[getSensors.toString()]: state => ({
		...state,
		sensorGroupById: {
			...state.sensorGroupById,
			configuredSensors: undefined,
			sensorLoaded: false,
		},
	}),
	[getSensorsResolved.toString()]: (state, action) => ({
		...state,
		sensorGroupById: {
			...state.sensorGroupById,
			configuredSensors: action.payload,
			sensorLoaded: true,
		},
	}),
	[getSensorsRejected.toString()]: state => ({
		...state,
		sensorGroupById: {
			...state.sensorGroupById,
			configuredSensors: undefined,
			sensorLoaded: false,
		},
	}),
	[getSingleSensorGroupByIdResolved.toString()]: (state, params) => ({
		...state,
		sensorGroupById: {
			initialized: true,
			sensorGroup: params.payload,
		},
	}),
	[getSingleSensorGroupByIdRejected.toString()]: state => ({
		...state,
		sensorGroupById: {
			...state.sensorGroupById,
			initialized: true,
		},
	}),
	[clearSensorGroupPairs.toString()]: state => ({
		...state,
		sensorGroupById: {
			initialized: false,
		},
	}),
	[getSensorGroupPairsResolved.toString()]: (
		state: SensorGroupStoreState,
		params: AnyAction,
	) => {
		const paginatedSensorGroupPairs = {
			...params.payload,
			status: STATUS_ENUM.SUCCESS,
		};
		return {
			...state,
			sensorGroupById: {
				...state?.sensorGroupById,
				paginatedSensorGroupPairs,
			},
		};
	},
	[getSensorGroupPairsRejected.toString()]: (
		state: SensorGroupStoreState,
	) => {
		const paginatedSensorGroupPairs = {
			status: STATUS_ENUM.ERROR,
		};
		return {
			...state,
			paginatedSensorGroupPairs,
		};
	},
	[getSensorGroupMap.toString()]: state => ({
		...state,
		sensorGroupMap: {
			status: STATUS_ENUM.PENDING,
			sensorGroupMap: {},
		},
	}),
	[clearSensorGroupMap.toString()]: state => ({
		...state,
		sensorGroupMap: {
			status: STATUS_ENUM.IDLE,
			sensorGroupMap: {},
		},
	}),
	[getSensorGroupMapResolved.toString()]: (state, params) => ({
		...state,
		sensorGroupMap: {
			status: STATUS_ENUM.SUCCESS,
			sensorGroupMap: params.payload,
		},
	}),
	[getSensorGroupMapRejected.toString()]: state => ({
		...state,
		sensorGroupMap: {
			...state.sensorGroupMap,
			status: STATUS_ENUM.ERROR,
		},
	}),
	[upsertSensorGroupConfig.toString()]: state => ({
		...state,
		upsertSensorGroup: {
			status: STATUS_ENUM.PENDING,
		},
	}),
	[upsertSensorGroupConfigResolved.toString()]: state => ({
		...state,
		upsertSensorGroup: {
			status: STATUS_ENUM.SUCCESS,
		},
	}),
	[upsertSensorGroupConfigRejected.toString()]: state => ({
		...state,
		upsertSensorGroup: {
			status: STATUS_ENUM.ERROR,
		},
	}),
};

export default createReducer(initialState, reducer);
